import { useMemo, useState } from "react"
import Icon from "../../components/Icon"
import { SkeletonList } from "../../components/Skeleton"
import { formatMoney } from "../../utils/format"
import { getCustomerInitials } from "./utbillsHelpers"
import { countCustomersAwaitingApproval, describeCustomerApprovalChip } from "./autopayApprovalDisplay"

const APPROVAL_CHIP_TONES = {
  amber: "bg-amber-100 text-amber-800",
  slate: "bg-slate-100 text-slate-600",
}

// The list opens on the people who matter most. The rest stay one tap away so a
// company with hundreds of customers still fits on a phone screen.
const PAGE_SIZE = 6

// The top of the list is where the money is, so the first three places are
// marked. Everyone else keeps a plain position number.
const rankStyle = (position) => {
  if (position === 1) return "bg-rose-600 text-white ring-rose-200"
  if (position === 2) return "bg-rose-100 text-rose-700 ring-rose-100"
  if (position === 3) return "bg-amber-100 text-amber-700 ring-amber-100"
  return "bg-slate-100 text-slate-500 ring-slate-100"
}

const matchesQuery = (customer, query) => {
  if (!query) return true
  return [customer.name, customer.phone, customer.idNumber]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(query)
}

function BalanceBadge({ customer }) {
  if (customer.amountDue > 0) {
    return (
      <>
        <p className="text-sm font-black leading-tight tabular-nums text-rose-600 sm:text-base">
          {formatMoney(customer.amountDue)}
        </p>
        <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Balance due</p>
      </>
    )
  }
  if (customer.creditAmount > 0) {
    return (
      <>
        <p className="text-sm font-black leading-tight tabular-nums text-teal-700 sm:text-base">
          {formatMoney(customer.creditAmount)}
        </p>
        <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">In credit</p>
      </>
    )
  }
  return (
    <>
      <p className="text-sm font-black leading-tight tabular-nums text-emerald-700 sm:text-base">0.00</p>
      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">All clear</p>
    </>
  )
}

function CustomerRow({ customer, position, largestDue, onOpenStatement }) {
  // Each bar is drawn against the largest debt in the list, so the ranking is
  // readable at a glance without reading any of the numbers.
  const barWidth = largestDue > 0 ? Math.max(3, (customer.amountDue / largestDue) * 100) : 0
  const displayName = customer.name || customer.phone || customer.idNumber || "Customer"
  const contact = customer.phone || customer.idNumber || "No contact saved"
  const billLabel = customer.billCount === 1 ? "1 bill" : `${customer.billCount} bills`
  const approvalChip = describeCustomerApprovalChip(customer.autopayApproval)

  return (
    <button
      type="button"
      onClick={() => onOpenStatement(customer)}
      aria-label={`Open the statement for ${displayName}`}
      className="group flex w-full flex-col gap-2 border-b border-slate-100 px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-teal-50/40 active:bg-teal-50 focus:outline-none focus-visible:bg-teal-50 sm:px-4"
    >
      <div className="flex items-center gap-2.5">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-black ring-1 ${rankStyle(position)}`}
        >
          {position}
        </span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-black text-slate-600 transition-colors group-hover:bg-teal-600 group-hover:text-white">
          {getCustomerInitials(displayName)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-black leading-tight text-slate-900">{displayName}</span>
          <span className="block truncate text-[11px] text-slate-500">
            {contact} · {billLabel}
            {customer.overdueBillCount > 0 ? ` · ${customer.overdueBillCount} overdue` : ""}
          </span>
          {approvalChip && (
            <span
              className={`mt-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                APPROVAL_CHIP_TONES[approvalChip.tone] || APPROVAL_CHIP_TONES.slate
              }`}
            >
              <Icon name="shield-exclamation" size="0.6rem" />
              {approvalChip.label}
            </span>
          )}
        </span>
        <span className="shrink-0 text-right">
          <BalanceBadge customer={customer} />
        </span>
      </div>

      {/* Indented to sit under the name: rank + avatar + both gaps = 4.75rem. */}
      <div className="flex items-center gap-2 pl-[4.75rem]">
        <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
          {barWidth > 0 && (
            <span
              className="block h-full rounded-full bg-gradient-to-r from-rose-400 to-rose-600"
              style={{ width: `${barWidth}%` }}
            />
          )}
        </span>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-teal-200 bg-teal-50 px-2 py-1 text-[10px] font-bold text-teal-700 transition-colors group-hover:border-teal-300 group-hover:bg-teal-600 group-hover:text-white">
          <Icon name="file-earmark-text" size="0.6rem" />
          Statement
          <Icon name="chevron-right" size="0.55rem" className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </button>
  )
}

// Everyone this company bills, biggest balance due first, each row opening that
// person's full statement.
function ProviderCustomersPanel({ customers = [], loading = false, onOpenStatement }) {
  const [query, setQuery] = useState("")
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const normalizedQuery = query.trim().toLowerCase()
  // The server already returns the list in balance order. Searching only removes
  // rows, so the order never rearranges itself while someone is typing, and each
  // person keeps the position they hold in the full list.
  const positionByCustomerId = useMemo(
    () => new Map(customers.map((customer, index) => [customer.customerId, index + 1])),
    [customers],
  )
  const matchedCustomers = useMemo(
    () => customers.filter((customer) => matchesQuery(customer, normalizedQuery)),
    [customers, normalizedQuery],
  )
  const totalDue = customers.reduce((sum, customer) => sum + Number(customer.amountDue || 0), 0)
  const owingCount = customers.filter((customer) => Number(customer.amountDue || 0) > 0).length
  const largestDue = Number(customers[0]?.amountDue || 0)
  const awaitingApprovalCount = countCustomersAwaitingApproval(customers)

  const shownCustomers = matchedCustomers.slice(0, visibleCount)
  const hiddenCount = matchedCustomers.length - shownCustomers.length

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 to-teal-50/60 px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-base font-black text-slate-900">
              <Icon name="people-fill" size="1rem" className="text-teal-600" />
              Customers
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {loading
                ? "Loading balances…"
                : customers.length === 0
                  ? "Nobody has been billed by this company yet."
                  : `${owingCount} of ${customers.length} still owe money. Highest first.`}
            </p>
            {!loading && awaitingApprovalCount > 0 && (
              <p className="mt-1 text-[11px] font-medium text-amber-700">
                {awaitingApprovalCount === 1 ? "1 customer has" : `${awaitingApprovalCount} customers have`} not
                approved auto-pay. Their bills wait until they approve or pay. Open a customer to ask them.
              </p>
            )}
          </div>
          <div className="shrink-0 rounded-xl border border-rose-100 bg-white px-2.5 py-1.5 text-right">
            <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Total due</p>
            <p className="text-sm font-black leading-tight tabular-nums text-rose-600">{formatMoney(totalDue)}</p>
          </div>
        </div>

        {customers.length > PAGE_SIZE && (
          <div className="relative mt-3">
            <Icon
              name="search"
              size="0.75rem"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setVisibleCount(PAGE_SIZE)
              }}
              placeholder="Search by name, phone or ID"
              aria-label="Search customers"
              className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-8 pr-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
            />
          </div>
        )}
      </div>

      {loading ? (
        <SkeletonList rows={4} className="p-3" />
      ) : customers.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-500">
          Issue a bill and the customer will appear here with their balance.
        </p>
      ) : matchedCustomers.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-500">No customer matches your search.</p>
      ) : (
        <>
          <div>
            {shownCustomers.map((customer) => (
              <CustomerRow
                key={customer.customerId}
                customer={customer}
                position={positionByCustomerId.get(customer.customerId) || 0}
                largestDue={largestDue}
                onOpenStatement={onOpenStatement}
              />
            ))}
          </div>

          {(hiddenCount > 0 || visibleCount > PAGE_SIZE) && (
            <div className="flex items-center justify-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-2.5">
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 py-1.5 text-xs font-bold text-teal-700 transition-colors hover:bg-teal-50"
                >
                  <Icon name="chevron-down" size="0.65rem" />
                  See {Math.min(PAGE_SIZE, hiddenCount)} more
                </button>
              )}
              {visibleCount > PAGE_SIZE && (
                <button
                  type="button"
                  onClick={() => setVisibleCount(PAGE_SIZE)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
                >
                  <Icon name="chevron-up" size="0.65rem" />
                  Show less
                </button>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default ProviderCustomersPanel
